import { ChevronDown, ChevronUp, Filter, RotateCcw, Search, SlidersHorizontal, Trash2 } from '../components/icons';
import { useMemo, useState } from 'react';
import { AnnualImpactTable } from '../components/AnnualImpactTable';
import { TrendChart } from '../components/TrendChart';
import { YearSelector } from '../components/YearSelector';
import { formatCurrency, formatMonth } from '../domain/format';
import {
  annualizedLineTotal,
  isPersonnelLedgerLine,
  personnelCostTotal,
  personnelPlanCost,
  summarizePeriod,
} from '../domain/scenarioEngine';
import { useScenario } from '../state/ScenarioContext';

export const ExpenseExplorerView = () => {
  const {
    data,
    inputs,
    setInputs,
    result,
    archivedPersonnelIds,
    archivePersonnel,
    archivePersonnelMany,
    restorePersonnel,
    restoreAllPersonnel,
  } = useScenario();
  const [selectedYear, setSelectedYear] = useState(data.asOfMonth.slice(0, 4));
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [control, setControl] = useState('all');
  const [showZero, setShowZero] = useState(false);
  const [showPayrollRollups, setShowPayrollRollups] = useState(false);
  const [showArchivedPersonnel, setShowArchivedPersonnel] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const yearMonths = result.months.filter(({ month }) => month.startsWith(`${selectedYear}-`));
  const forecastMonths = data.months.filter(({ kind }) => kind === 'forecast');
  const yearForecastMonths = forecastMonths.filter(({ month }) => month.startsWith(`${selectedYear}-`));
  const defaultStartMonth = yearForecastMonths[0]?.month ?? forecastMonths[0]?.month ?? data.asOfMonth;
  const periodBaseline = summarizePeriod(yearMonths, 'baseline');
  const periodScenario = summarizePeriod(yearMonths, 'scenario');
  const categories = useMemo(
    () => [...new Set(data.expenseLines.filter((line) => line.modelRole !== 'financing_schedule').map((line) => line.category))].sort(),
    [data.expenseLines],
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return data.expenseLines
      .filter((line) => line.modelRole !== 'financing_schedule')
      .filter((line) => showPayrollRollups || line.category !== 'People & payroll')
      .filter((line) => category === 'all' || line.category === category)
      .filter((line) => control === 'all' || line.controllability === control)
      .filter((line) => showZero || Math.abs(annualizedLineTotal(data, line, undefined, selectedYear)) >= 0.005)
      .filter((line) => !needle || `${line.name} ${line.category}`.toLowerCase().includes(needle))
      .sort((a, b) => annualizedLineTotal(data, b, undefined, selectedYear) - annualizedLineTotal(data, a, undefined, selectedYear));
  }, [category, control, data, query, selectedYear, showPayrollRollups, showZero]);
  const selected = data.expenseLines.find((line) => line.id === selectedId) ?? null;
  const archivedPersonnelSet = new Set(archivedPersonnelIds);
  const archivedPersonnel = (data.personnel ?? []).filter((record) => archivedPersonnelSet.has(record.id));
  const visiblePersonnel = (data.personnel ?? []).filter((record) => !archivedPersonnelSet.has(record.id));
  const inactivePersonnelIds = visiblePersonnel
    .filter((record) => forecastMonths.every(({ month }) => (record.amounts[month] ?? 0) === 0))
    .map((record) => record.id);
  const personnelRows = visiblePersonnel.map((record) => {
    const plan = personnelCostTotal(data, record, undefined, selectedYear);
    const scenario = personnelCostTotal(data, record, inputs, selectedYear);
    const salaryMonths = yearForecastMonths.length ? yearForecastMonths : yearMonths;
    const firstPaidMonth = salaryMonths.find(({ month }) => (record.amounts[month] ?? 0) > 0)?.month;
    const monthlySalary = firstPaidMonth ? (record.amounts[firstPaidMonth] ?? 0) : 0;
    const monthlyLoaded = firstPaidMonth ? personnelPlanCost(data, record, firstPaidMonth) : 0;
    return { record, plan, scenario, monthlySalary, monthlyLoaded };
  }).sort((a, b) => b.plan - a.plan || a.record.name.localeCompare(b.record.name));

  const updateLine = (id: string, changes: { enabled?: boolean; cutPercent?: number; startMonth?: string }) => {
    const isPayrollLine = isPersonnelLedgerLine(id);
    setInputs((current) => ({
      ...current,
      personnelAdjustments: changes.enabled && isPayrollLine ? {} : current.personnelAdjustments,
      expenseAdjustments: {
        ...current.expenseAdjustments,
        [id]: {
          enabled: current.expenseAdjustments[id]?.enabled ?? false,
          cutPercent: current.expenseAdjustments[id]?.cutPercent ?? 10,
          startMonth: current.expenseAdjustments[id]?.startMonth ?? defaultStartMonth,
          ...changes,
        },
      },
    }));
  };

  const updatePerson = (
    id: string,
    changes: { enabled?: boolean; terminationMonth?: string; severanceMonths?: number },
  ) => {
    const record = data.personnel?.find((person) => person.id === id);
    setInputs((current) => ({
      ...current,
      expenseAdjustments: changes.enabled
        ? Object.fromEntries(Object.entries(current.expenseAdjustments).filter(([lineId]) =>
            !isPersonnelLedgerLine(lineId)))
        : current.expenseAdjustments,
      personnelAdjustments: {
        ...(current.personnelAdjustments ?? {}),
        [id]: {
          enabled: current.personnelAdjustments?.[id]?.enabled ?? false,
          terminationMonth: current.personnelAdjustments?.[id]?.terminationMonth ?? record?.expectedLeaveMonth ?? defaultStartMonth,
          severanceMonths: current.personnelAdjustments?.[id]?.severanceMonths ?? 0,
          ...changes,
        },
      },
    }));
  };

  return (
    <div className="page-stack">
      <section className="page-heading compact">
        <div>
          <p className="eyebrow">Every expense line · 12-month decisions</p>
          <h2>Cut a cost in a specific month and see the monthly and annual result.</h2>
          <p>Actuals stay locked. Select a year, reduction, and start month; employee exits include loaded employer cost and editable severance.</p>
        </div>
        <YearSelector months={data.months} value={selectedYear} onChange={setSelectedYear} />
      </section>

      <AnnualImpactTable months={result.months} id="expense-explorer" />

      <section className="metric-grid three">
        <div className="metric-card"><p className="eyebrow">{selectedYear} plan spend</p><strong className="metric-value">{formatCurrency(periodBaseline.expenses, { compact: true })}</strong><p className="metric-context">12 months · actuals plus forecast</p></div>
        <div className="metric-card"><p className="eyebrow">{selectedYear} scenario spend</p><strong className="metric-value">{formatCurrency(periodScenario.expenses, { compact: true })}</strong><p className="metric-context">After line and employee decisions</p></div>
        <div className="metric-card"><p className="eyebrow">{selectedYear} savings</p><strong className="metric-value positive-text">{formatCurrency(periodScenario.expenseSavings, { compact: true })}</strong><p className="metric-context">Monthly changes rolled into the year</p></div>
      </section>

      <section className="panel chart-panel">
        <div className="panel-heading"><div><p className="eyebrow">Monthly expense impact · {selectedYear}</p><h3>Plan vs working scenario</h3></div></div>
        <TrendChart
          labels={yearMonths.map(({ month }) => month)}
          series={[
            { name: 'Plan', values: yearMonths.map(({ baselineExpenses }) => baselineExpenses), tone: 'baseline', dashed: true },
            { name: 'Scenario', values: yearMonths.map(({ scenarioExpenses }) => scenarioExpenses), tone: 'primary' },
          ]}
          ariaLabel={`Monthly plan and scenario expenses for ${selectedYear}`}
        />
      </section>

      <section className="panel table-panel">
        <div className="panel-heading personnel-heading">
          <div><p className="eyebrow">Confirmed current team · salary and leave plan</p><h3>See each salary, expected exit, and what a different cut date changes.</h3><p className="panel-copy">The eight-person roster and planned salary schedule are confirmed from Emp Costs. Expected exit is the first permanently unpaid month; loaded cost adds payroll tax, bonus accrual, and health insurance.</p></div>
          <div className="personnel-cleanup-actions">
            {inactivePersonnelIds.length ? <button className="button danger-subtle" onClick={() => archivePersonnelMany(inactivePersonnelIds)}><Trash2 size={15} />Remove {inactivePersonnelIds.length} inactive</button> : null}
            {archivedPersonnel.length ? <button className="button ghost" onClick={() => setShowArchivedPersonnel((current) => !current)}><RotateCcw size={15} />{showArchivedPersonnel ? 'Hide' : 'View'} {archivedPersonnel.length} removed</button> : null}
            <span>{personnelRows.length} confirmed team records</span>
          </div>
        </div>
        <div className="responsive-table">
          <table className="decision-table">
            <thead><tr><th>Cut</th><th>Employee</th><th>Expected exit</th><th className="numeric">Next salary</th><th className="numeric">Next loaded cost</th><th className="numeric">{selectedYear} plan</th><th>Cut month</th><th className="numeric">Severance</th><th className="numeric">Scenario</th><th className="numeric">Savings</th><th><span className="sr-only">Remove</span></th></tr></thead>
            <tbody>
              {personnelRows.map(({ record, plan, scenario, monthlySalary, monthlyLoaded }) => {
                const adjustment = inputs.personnelAdjustments?.[record.id] ?? { enabled: false, terminationMonth: record.expectedLeaveMonth ?? defaultStartMonth, severanceMonths: 0 };
                const hasNoForecastSalary = forecastMonths.every(({ month }) => (record.amounts[month] ?? 0) === 0);
                const cutAfterPlannedExit = Boolean(
                  adjustment.enabled &&
                  record.expectedLeaveMonth &&
                  adjustment.terminationMonth >= record.expectedLeaveMonth &&
                  Math.abs(plan - scenario) < 0.005,
                );
                return (
                  <tr key={record.id} className={adjustment.enabled ? 'edited-row' : ''}>
                    <td><label className="switch"><input type="checkbox" checked={adjustment.enabled} onChange={(event) => updatePerson(record.id, { enabled: event.target.checked })} aria-label={`Model an exit for ${record.name}`} /><span aria-hidden="true" /></label></td>
                    <td><strong>{record.name}</strong><small>{record.department ?? record.category} · {record.role ?? record.category}</small></td>
                    <td><span className={`personnel-status ${record.expectedLeaveMonth ? 'planned-exit' : 'ongoing'}`}>{record.expectedLeaveMonth ? (hasNoForecastSalary ? `Before ${formatMonth(record.expectedLeaveMonth)}` : formatMonth(record.expectedLeaveMonth)) : 'Ongoing'}</span></td>
                    <td className="numeric">{formatCurrency(monthlySalary)}</td>
                    <td className="numeric">{formatCurrency(monthlyLoaded)}</td>
                    <td className="numeric">{formatCurrency(plan)}</td>
                    <td><select className="table-select" value={adjustment.terminationMonth} onChange={(event) => updatePerson(record.id, { terminationMonth: event.target.value, enabled: true })}>{forecastMonths.map(({ month }) => <option key={month} value={month}>{formatMonth(month)}</option>)}</select></td>
                    <td className="numeric"><label className="months-input"><input type="number" min="0" max="24" step="0.5" value={adjustment.severanceMonths} onChange={(event) => updatePerson(record.id, { severanceMonths: Number(event.target.value), enabled: true })} aria-label={`Severance salary months for ${record.name}`} /><span>mo</span></label></td>
                    <td className="numeric"><strong>{formatCurrency(scenario)}</strong>{cutAfterPlannedExit ? <small className="scenario-warning">Already scheduled out</small> : null}</td>
                    <td className={`numeric ${plan - scenario >= 0 ? 'positive-text' : 'negative-text'}`}>{formatCurrency(plan - scenario)}{cutAfterPlannedExit ? <small className="scenario-warning">No additional savings after {formatMonth(record.expectedLeaveMonth!)}</small> : null}</td>
                    <td><button className="icon-button danger" onClick={() => archivePersonnel(record.id)} aria-label={`Remove ${record.name} from the dashboard`} title="Remove from dashboard"><Trash2 size={15} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!personnelRows.length ? <div className="empty-state"><Trash2 size={23} /><strong>No visible employee records</strong><span>Open removed employees below to restore someone.</span></div> : null}
        {showArchivedPersonnel && archivedPersonnel.length ? (
          <div className="archived-personnel-panel">
            <div className="archived-personnel-heading"><div><strong>Removed employees</strong><span>Hidden from this dashboard only; workbook data and payroll forecasts are unchanged.</span></div><button className="button ghost" onClick={restoreAllPersonnel}><RotateCcw size={14} />Restore all</button></div>
            <div className="archived-personnel-list">
              {archivedPersonnel.map((record) => <div key={record.id}><span><strong>{record.name}</strong><small>{record.category}</small></span><button className="button ghost compact" onClick={() => restorePersonnel(record.id)}><RotateCcw size={13} />Restore</button></div>)}
            </div>
          </div>
        ) : null}
        <p className="table-footnote">Remove only cleans up the dashboard; it does not delete workbook rows or create payroll savings. “Remove inactive” selects employees with no salary in any remaining forecast month. Employee exits and People &amp; payroll account cuts are mutually exclusive; activating one clears the other so savings cannot be counted twice.</p>
      </section>

      <section className="panel table-panel">
        <div className="toolbar">
          <label className="search-field"><Search size={17} aria-hidden="true" /><span className="sr-only">Search expenses</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search expense or category…" /></label>
          <label className="select-field"><Filter size={16} aria-hidden="true" /><span className="sr-only">Filter category</span><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="all">All categories</option>{categories.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="select-field"><SlidersHorizontal size={16} aria-hidden="true" /><span className="sr-only">Filter controllability</span><select value={control} onChange={(event) => setControl(event.target.value)}><option value="all">All control levels</option><option value="high">High control</option><option value="medium">Medium control</option><option value="low">Low control</option><option value="committed">Committed</option></select></label>
          <label className="zero-line-toggle"><input type="checkbox" checked={showZero} onChange={(event) => setShowZero(event.target.checked)} />Show $0 lines</label>
          <label className="zero-line-toggle"><input type="checkbox" checked={showPayrollRollups} onChange={(event) => setShowPayrollRollups(event.target.checked)} />Show payroll ledger rollups</label>
          <span className="result-count">{filtered.length} decision lines</span>
        </div>

        <div className="responsive-table">
          <table className="decision-table">
            <thead><tr><th>Cut</th><th>Expense</th><th>Control</th><th className="numeric">{selectedYear} plan</th><th className="numeric">Reduction</th><th>Start month</th><th className="numeric">Scenario</th><th><span className="sr-only">Inspect</span></th></tr></thead>
            <tbody>
              {filtered.map((line) => {
                const adjustment = inputs.expenseAdjustments[line.id] ?? { enabled: false, cutPercent: 10, startMonth: defaultStartMonth };
                const baseline = annualizedLineTotal(data, line, undefined, selectedYear);
                const scenario = annualizedLineTotal(data, line, inputs, selectedYear);
                const isSelected = selectedId === line.id;
                return (
                  <tr key={line.id} className={adjustment.enabled ? 'edited-row' : ''}>
                    <td><label className="switch"><input type="checkbox" checked={adjustment.enabled} onChange={(event) => updateLine(line.id, { enabled: event.target.checked })} aria-label={`Apply a cut to ${line.name}`} /><span aria-hidden="true" /></label></td>
                    <td><strong>{line.name}</strong><small>{line.category}</small></td>
                    <td><span className={`control-badge ${line.controllability}`}>{line.controllability}</span></td>
                    <td className="numeric">{formatCurrency(baseline)}</td>
                    <td className="numeric"><label className="percent-input"><span className="sr-only">Cut percent for {line.name}</span><input type="number" min="0" max="100" step="1" value={adjustment.cutPercent} onChange={(event) => updateLine(line.id, { cutPercent: Number(event.target.value), enabled: true })} /><span>%</span></label></td>
                    <td><select className="table-select" value={adjustment.startMonth ?? defaultStartMonth} onChange={(event) => updateLine(line.id, { startMonth: event.target.value, enabled: true })}>{forecastMonths.map(({ month }) => <option key={month} value={month}>{formatMonth(month)}</option>)}</select></td>
                    <td className="numeric"><strong>{formatCurrency(scenario)}</strong>{adjustment.enabled ? <small className="savings">Save {formatCurrency(baseline - scenario)}</small> : null}</td>
                    <td><button className="icon-button" onClick={() => setSelectedId(isSelected ? null : line.id)} aria-label={`Inspect ${line.name}`} aria-expanded={isSelected}>{isSelected ? <ChevronUp size={17} /> : <ChevronDown size={17} />}</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!filtered.length ? <div className="empty-state"><Search size={24} /><strong>No matching expenses</strong><span>Try a different name or category.</span></div> : null}
        <p className="table-footnote">Payroll ledger rollups are hidden by default because the employee table above is the decision-level view. They remain included once—and only once—in company expense totals. Turn on “Show payroll ledger rollups” to inspect the accounting accounts; edits at the payroll-account and employee level are mutually exclusive.</p>
      </section>

      {selected ? (
        <section className="panel detail-panel">
          <div className="panel-heading"><div><p className="eyebrow">12-month line-item drilldown</p><h3>{selected.name}</h3></div><span className={`confidence-badge ${selected.confidence}`}>{selected.confidence} confidence</span></div>
          <div className="detail-grid">
            <div><dl className="definition-list"><div><dt>Category</dt><dd>{selected.category}</dd></div><div><dt>Source</dt><dd>{selected.source}</dd></div><div><dt>Source detail</dt><dd>{selected.sourceDetail}</dd></div><div><dt>Controllability</dt><dd>{selected.controllability}</dd></div></dl><p className="detail-note"><SlidersHorizontal size={16} />{selected.notes ?? 'Review contract terms, timing, and operational dependencies before committing the cut.'}</p></div>
            <TrendChart
              labels={yearMonths.map(({ month }) => month)}
              series={[
                { name: 'Plan', values: yearMonths.map(({ month }) => selected.amounts[month] ?? 0), tone: 'baseline', dashed: true },
                { name: 'Scenario', values: yearMonths.map(({ month, kind }) => {
                  const adjustment = inputs.expenseAdjustments[selected.id];
                  const applies = kind === 'forecast' && adjustment?.enabled && (!adjustment.startMonth || month >= adjustment.startMonth);
                  return (selected.amounts[month] ?? 0) * (applies ? 1 - Math.min(100, Math.max(0, adjustment.cutPercent)) / 100 : 1);
                }), tone: 'primary' },
              ]}
              ariaLabel={`Monthly plan and scenario spending for ${selected.name} in ${selectedYear}`}
              height={220}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
};
